// GraphQL
import { client } from '../../../../gql/client';
import {
  GET_SURVEILLANCE_ROUTE_FOR_EDIT,
  GET_ROUTE_SEGMENT,
  GET_ROUTE_SEGMENTS,
} from '../../../../gql/queries/surveillanceRoute';
import { fetchActionFormCatalogs } from './loader';

// Offline
import { getQueue } from '../../../../offline/offlineQueue';
import {
  hasSyntheticId,
  mergePendingSegments,
} from '../../../../offline/pendingSegmentsFromQueue';

// Types
import { OrderState, OrderType } from '../../../../types/orders';

// Utils
import { pkFormatter } from '../../../../utils/transform';
import { getSessionStorageUser } from '../../../../utils/sessionStorage';
import { normalizeDirectionForQuery } from '../../../../utils/surveillanceRoute';

export type BasicActionSeedParams = {
  routeId: string;
  orderParentId: string;
  orderParentRoid: string;
  fromSegmentId?: string | null;
  description?: string | null;
  /** Sin red: toda lectura es de caché y ninguna tumba el conjunto. */
  offline: boolean;
};

export type BasicActionSeed = {
  /** Lo que va a `NEW_ACTION_DATA`: el contrato que leen las pestañas del wizard. */
  seed: Record<string, unknown>;
  /** Input de SAVE_ACTION sin `clientOpId` (solo lo usa el camino online). */
  input: Record<string, unknown>;
};

type SeedSegment = {
  /** Multi-carretera (2026-08-15): la básica hereda la carretera del TRAMO. */
  roadId?: string | null;
  /** Por dónde fue, si cruzó. El último trozo es donde el operario paró. */
  roadStretches?:
    | { roadId: string; pkInitKm: number; pkInitMeter: number; pkEndKm: number; pkEndMeter: number }[]
    | null;
  pkInitKm?: number | null;
  pkInitMeter?: number | null;
  pkEndKm?: number | null;
  pkEndMeter?: number | null;
  direction?: string | null;
  lane?: string | null;
};

/**
 * Catálogos de la fase 1 (las pestañas Ubicación/Edición leen las listas de
 * `NEW_ACTION_DATA`). Sin red, si no están en caché la semilla sale sin listas
 * pero con los ids: los desplegables quedan vacíos y la actuación se crea bien
 * al sincronizar. Con red un fallo sí es un fallo (el router action vuelve al
 * recorrido).
 */
const readCatalogs = async (offline: boolean): Promise<Record<string, any>> => {
  try {
    return await fetchActionFormCatalogs();
  } catch (err) {
    if (!offline) throw err;
    console.warn('[basic-action] sin catálogos en caché, semilla sin listas', err);
    return {};
  }
};

/**
 * Herencia de ubicación: tramo que dispara la cadena > ÚLTIMO tramo del
 * recorrido > PK del propio RV (decisión usuario 2026-07-21).
 *
 * Un tramo `pending-*` solo existe en la cola simple (se inició sin red): se
 * lee de ahí, con el mismo derivador que pinta su fila ⏳ en el detalle.
 */
const readSegment = async (
  routeId: string,
  fromSegmentId: string | null | undefined,
  offline: boolean
): Promise<SeedSegment | null> => {
  try {
    if (fromSegmentId && hasSyntheticId({ id: fromSegmentId })) {
      const queue = await getQueue();
      const pending = mergePendingSegments([], queue, routeId);
      return pending.find((s) => s.id === fromSegmentId) ?? null;
    }
    if (fromSegmentId) {
      const segRes = await client.query({
        query: GET_ROUTE_SEGMENT,
        variables: { id: fromSegmentId },
        fetchPolicy: 'cache-first',
      });
      const server = segRes.data?.routeSegment ?? null;
      if (!server) return null;
      // Un cierre encolado sin red no toca la caché de Apollo: se aplica desde
      // la cola, igual que hace la fila ⏳ del detalle. Con red la cola está
      // vacía y el servidor gana — esto no cambia nada.
      const merged = mergePendingSegments([server], await getQueue(), routeId);
      return merged[0] ?? server;
    }
    const segsRes = await client.query({
      query: GET_ROUTE_SEGMENTS,
      variables: { surveillanceRouteId: routeId },
      fetchPolicy: offline ? 'cache-first' : 'network-only',
    });
    const segs = segsRes.data?.routeSegments ?? [];
    return segs.length
      ? [...segs].sort(
          (a: { indexType?: number }, b: { indexType?: number }) =>
            (a.indexType ?? 0) - (b.indexType ?? 0)
        )[segs.length - 1]
      : null;
  } catch {
    // Sin tramos: seguimos con los datos del RV.
    return null;
  }
};

const readRoute = async (routeId: string, offline: boolean) => {
  try {
    const routeRes = await client.query({
      query: GET_SURVEILLANCE_ROUTE_FOR_EDIT,
      variables: { id: routeId },
      fetchPolicy: 'cache-first',
    });
    return routeRes.data?.surveillanceRoute ?? null;
  } catch (err) {
    if (!offline) throw err;
    // Sin red y sin recorrido en caché: la semilla sigue con PK 0 / sin
    // carretera (mejor eso que tumbar el auto-create). Con red un fallo aquí
    // es un fallo real y debe abortar, igual que `readCatalogs`.
    console.warn('[basic-action] sin recorrido en caché, semilla sin PK/carretera', err);
    return null;
  }
};

const fmtPk = (km: number, m: number) => `${km}+${String(m).padStart(3, '0')}`;

/**
 * Semilla de la actuación básica del RV — la MISMA para los dos caminos.
 *
 * Online, `createBasicAction` la usa para el `SAVE_ACTION` y para sembrar el
 * wizard con los ids reales. Sin red, `seedBasicActionOffline` la guarda con
 * `_isOffline` y el wizard trabaja sobre ella; la fase 2 la encola y el replay
 * construye el `SAVE_ACTION` desde ella (`buildSaveActionInputFromForm`). Por
 * eso lleva `registeredByUserId`: el replay lo lee de aquí y sin él el backend
 * descarta la actuación. Y por eso lleva `containerType`: es el dato por el que
 * el guardado, el resumen y el PDF saben que es una básica.
 */
export async function buildBasicActionSeed(
  p: BasicActionSeedParams
): Promise<BasicActionSeed> {
  const { routeId, orderParentId, orderParentRoid, fromSegmentId, offline } = p;

  const [catalogs, route] = await Promise.all([
    readCatalogs(offline),
    readRoute(routeId, offline),
  ]);
  const segment = await readSegment(routeId, fromSegmentId, offline);

  // 🔴 La actuación nace en el punto de PARADA, no con el tramo entero
  // (decisión usuario 2026-08-27): un tramo de patrulla son kilómetros. Sin PK
  // de cierre (tramo aún abierto) se cae a su inicio.
  const pkStopKm = segment?.pkEndKm ?? segment?.pkInitKm ?? route?.pkEndKm ?? 0;
  const pkStopMeter = segment?.pkEndMeter ?? segment?.pkInitMeter ?? route?.pkEndMeter ?? 0;
  const pkInitKm = pkStopKm;
  const pkInitMeter = pkStopMeter;
  const pkEndKm = pkStopKm;
  const pkEndMeter = pkStopMeter;

  const concessionId = route?.concessionIDs?.[0];
  // Multi-carretera: la carretera DEL TRAMO (y con trozos, la del ÚLTIMO — la
  // actuación se hace donde se paró). Un tramo antiguo sin carretera cae a la
  // primera del recorrido.
  const lastStretch = segment?.roadStretches?.length
    ? segment.roadStretches[segment.roadStretches.length - 1]
    : null;
  const roadId = lastStretch?.roadId ?? segment?.roadId ?? route?.roadIDs?.[0];
  const concessionObj = (catalogs.concessions ?? []).find(
    (c: { id: string }) => c.id === concessionId
  );
  const roadObj = (catalogs.roads ?? []).find((r: { id: string }) => r.id === roadId);

  // Operario que registra: en la básica no hay UI para elegirlo → el usuario
  // logueado (el backend lo exige para rol Tablet).
  const registeredByUserId = getSessionStorageUser()?.user?.id;

  // Sentido heredado del tramo, en el vocabulario que entiende el control de
  // Ubicación (top/down/both/unspecified). Los tramos en BD llevan top/down,
  // pero hay datos legacy con ascending/descending: pasados crudos, el control
  // no los reconoce, el campo queda vacío y el validador central bloquea el
  // envío final del wizard sin ningún aviso.
  const direction = normalizeDirectionForQuery(segment?.direction);

  const input = {
    parentOrderId: orderParentId,
    state: OrderState.Partial,
    creationDate: new Date().toISOString(),
    pkInitKm,
    pkInitMeter,
    pkEndKm,
    pkEndMeter,
    // `track` y `lane` son REQUIRED en el modelo Prisma Action. Troncal por
    // defecto; carril/sentido heredados del tramo si hay.
    track: 'trunk',
    lane: segment?.lane ?? 'unspecified',
    ...(direction && { direction }),
    ...(concessionId && { concessionId }),
    ...(roadId && { roadId }),
    description: p.description ?? '',
    ...(registeredByUserId && { registeredByUserId }),
  };

  const seed = {
    ...catalogs,
    orderParentId,
    orderParentRoid,
    routeId,
    // Discriminador de la básica (lo lee `isBasicAction`).
    containerType: OrderType.SurveillanceRoute,
    description: p.description ?? '',
    concession: concessionId ?? '',
    road: roadId ?? '',
    // Espejo de onSelectConcession/onSelectRoad del flujo normal.
    ...(concessionObj?.roadIDs?.length
      ? {
          roads: (catalogs.roads ?? []).filter((r: { id: string }) =>
            concessionObj.roadIDs.includes(r.id)
          ),
        }
      : {}),
    ...(roadObj
      ? {
          pkInitRoad: pkFormatter(roadObj.pkInitKm, roadObj.pkInitMeter, true),
          pkEndRoad: pkFormatter(roadObj.pkEndKm, roadObj.pkEndMeter, true),
          actionRoadDirections: roadObj.directions ?? [],
        }
      : {}),
    pkInit: fmtPk(pkInitKm, pkInitMeter),
    pkEnd: fmtPk(pkEndKm, pkEndMeter),
    track: 'trunk',
    lane: segment?.lane ?? 'unspecified',
    ...(direction ? { direction } : {}),
    ...(fromSegmentId ? { fromSegmentId } : {}),
    ...(registeredByUserId ? { registeredByUserId } : {}),
  };

  return { seed, input };
}
